import {
  Dispatch,
  SetStateAction,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { FormStoreInstance } from '@components/form/create-form-store';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconFieldCustom } from '@components/icons/field-custom';
import { IconFormIndicatorInfoCurrentColor } from '@components/icons/form-indicator-info-current-color';
import { IconTrash } from '@components/icons/trash';
import { InfoBox } from '@components/info-box';
import {
  IntegrationEndpointDetail,
  IntegrationEndpointVariable,
} from '@components/integrations/integrations-types';
import { getStringValue } from '@components/integrations/lists/dynamic-options-utils';
import { LabelTextGroup } from '@components/label-text-group';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  FieldMappingGroup,
  FieldType,
  VariableValue,
} from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';

import { useCustomFieldList } from 'api/app-marketplace/hooks';
import { CustomField, CustomFieldOption, stableContextKey } from 'api/app-marketplace/requests';
import { CustomFieldOptionValue, CustomFieldSingleValue, SavedCustomField } from 'api/types';

import { useAddCustomFieldModal } from './add-custom-field-modal';
import { formatCustomFieldValue } from './format-custom-field-value';
import { IntegrationField, MultiSelectFieldValue } from './integration-field';

type CustomFieldRow = {
  fieldId: string;
  type: string;
  template: string;
  isArray: boolean;
  parentContextId: string;
  name: string;
  valueMapping?: CustomFieldSingleValue | CustomFieldOptionValue[];
  options?: CustomFieldOption[];
};

const CUSTOM_FIELDS_PREFIX = 'customFields';

const customFieldRowFormId = (fieldId: string) => `${CUSTOM_FIELDS_PREFIX}__${fieldId}`;

export const resolveParentContext = (
  mapping: Record<string, string> | undefined,
  readEndpointValue: (endpointVariableName: string) => VariableValue | string | undefined
): Record<string, string> => {
  if (!mapping) return {};
  const result: Record<string, string> = {};
  for (const [discoveryVar, endpointVar] of Object.entries(mapping)) {
    if (!endpointVar) continue;
    const value = (getStringValue(readEndpointValue(endpointVar)) ?? '').trim();
    if (value) result[discoveryVar] = value;
  }
  return result;
};

export const useCustomFieldParentContext = (
  endpointData: IntegrationEndpointDetail | undefined,
  formStore: FormStoreInstance,
  savedRequestData?: Record<string, VariableValue | string | undefined>
): Record<string, string> => {
  const mapping = endpointData?.customFieldParentVariableMapping ?? {};
  const formKeys = Object.values(mapping)
    .filter(Boolean)
    .map((name) => `requestData__${name}`);
  const formValues = formStore.useWatchValues(formKeys);
  return resolveParentContext(mapping, (endpointVar) => {
    const key = `requestData__${endpointVar}`;
    // Once the request field above has registered, use its live value — even when
    // empty, so clearing it re-hides the custom fields. Only before it registers
    // do we seed from the saved value, to avoid an empty-state flash on open.
    if (key in formValues) {
      return formValues[key] as VariableValue | string | undefined;
    }
    return savedRequestData?.[endpointVar];
  });
};

type CustomFieldsContextProps = {
  endpointData: IntegrationEndpointDetail | undefined;
  savedCustomFields: SavedCustomField[];
  parentContext?: Record<string, string>;
};

const useCustomFieldsContext = ({
  endpointData,
  savedCustomFields,
  parentContext,
}: CustomFieldsContextProps) => {
  const customFieldConfigShortId = endpointData?.customFieldConfigShortId ?? null;
  const customerIntegrationShortId =
    typeof endpointData?.integration === 'string' ? endpointData.integration : '';
  const effectiveParentContext = parentContext ?? {};

  const customFieldListQuery = useCustomFieldList(
    customerIntegrationShortId || undefined,
    customFieldConfigShortId,
    effectiveParentContext
  );
  const knownFields = customFieldListQuery.data?.fields ?? [];
  const knownFieldIds = new Set(knownFields.map((field) => field.fieldId));
  const fieldNameById = new Map(knownFields.map((field) => [field.fieldId, field.name]));
  const optionsByFieldId = new Map(knownFields.map((field) => [field.fieldId, field.options]));
  const isFieldsLoaded = Boolean(customFieldListQuery.data);

  const initialRows = useMemo<CustomFieldRow[]>(
    () =>
      savedCustomFields.map((row) => ({
        fieldId: row.fieldId,
        type: row.type,
        template: row.template,
        isArray: row.isArray,
        parentContextId: row.parentContextId ?? '',
        name: row.name ?? row.fieldId,
        valueMapping:
          typeof row.valueMapping === 'string'
            ? { value: row.valueMapping, variables: {} }
            : row.valueMapping,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    customFieldConfigShortId,
    customerIntegrationShortId,
    parentContext: effectiveParentContext,
    knownFieldIds,
    fieldNameById,
    optionsByFieldId,
    initialRows,
    isFieldsLoaded,
  };
};

export type CustomFieldsEditorHandle = {
  getCustomFields: () => SavedCustomField[];
};

type CustomFieldsEditorProps = {
  endpointData: IntegrationEndpointDetail | undefined;
  savedCustomFields: SavedCustomField[];
  formStore: FormStoreInstance;
  variableGroupsByType: (fieldType: FieldType) => FieldMappingGroup[];
  fieldMappingId: string;
  parentContext?: Record<string, string>;
  onDirtyChange?: () => void;
};

export const CustomFieldsEditor = forwardRef<CustomFieldsEditorHandle, CustomFieldsEditorProps>(
  (
    {
      endpointData,
      savedCustomFields,
      formStore,
      variableGroupsByType,
      fieldMappingId,
      parentContext,
      onDirtyChange,
    },
    ref
  ) => {
    const {
      customFieldConfigShortId,
      customerIntegrationShortId,
      knownFieldIds,
      fieldNameById,
      optionsByFieldId,
      initialRows,
      isFieldsLoaded,
      parentContext: effectiveParentContext,
    } = useCustomFieldsContext({
      endpointData,
      savedCustomFields,
      parentContext,
    });

    const { t } = useScopedTranslate(locale);
    const [rows, setRows] = useState<CustomFieldRow[]>(initialRows);

    const handleSetRows: typeof setRows = (update) => {
      setRows(update);
      onDirtyChange?.();
    };

    useImperativeHandle(
      ref,
      () => ({
        getCustomFields: () => {
          const formValues = formStore.getState().values as Record<string, unknown>;
          // Only save rows for the current parent context. A stale row from a
          // previous context (e.g. a different table) would otherwise send an
          // invalid field id when the action runs.
          const currentContextId = stableContextKey(effectiveParentContext);
          return rows
            .filter((row) => row.parentContextId === currentContextId)
            .map((row) => {
              const base = {
                fieldId: row.fieldId,
                name: row.name,
                type: row.type,
                template: row.template,
                isArray: row.isArray,
                parentContextId: row.parentContextId,
              };
              const formValue = formValues[customFieldRowFormId(row.fieldId)];
              // A multi-select already stores {value, label} pairs.
              if (Array.isArray(formValue)) {
                return { ...base, valueMapping: formValue as CustomFieldOptionValue[] };
              }
              const valueMapping: CustomFieldSingleValue =
                formValue && typeof formValue === 'object'
                  ? (formValue as CustomFieldSingleValue)
                  : {
                      value: typeof formValue === 'string' ? formValue : '',
                      variables: {},
                    };
              // Save the picked option's label so the read-only view needn't re-fetch.
              const label = optionsByFieldId
                .get(row.fieldId)
                ?.find((option) => String(option.id ?? '') === valueMapping.value)?.label;
              return {
                ...base,
                valueMapping: label ? { ...valueMapping, label } : valueMapping,
              };
            });
        },
      }),
      [rows, formStore, optionsByFieldId, effectiveParentContext]
    );

    if (!customFieldConfigShortId || !customerIntegrationShortId) {
      return null;
    }

    const displayRows = rows.map((row) => ({
      ...row,
      name:
        row.name && row.name !== row.fieldId
          ? row.name
          : fieldNameById.get(row.fieldId) ?? row.fieldId,
      options: row.options ?? optionsByFieldId.get(row.fieldId),
    }));

    const parentVariableMapping = endpointData?.customFieldParentVariableMapping ?? {};
    const endpointVariables = endpointData?.variables ?? [];
    const labelForEndpointVariable = (endpointVariableName: string) =>
      endpointVariables.find((variable) => variable.name === endpointVariableName)?.label ??
      endpointVariableName;
    const missingParentFields = Object.entries(parentVariableMapping)
      .filter(([discoveryVariable]) => !effectiveParentContext[discoveryVariable])
      .map(([, endpointVariable]) => labelForEndpointVariable(endpointVariable));

    return (
      <Stack space={4}>
        <Separator />
        <HideContainer title={t('customFields')} icon={<IconFieldCustom />}>
          <CustomFieldsSection
            integrationShortId={customerIntegrationShortId}
            configShortId={customFieldConfigShortId}
            parentContext={effectiveParentContext}
            rows={displayRows}
            setRows={handleSetRows}
            variableGroupsByType={variableGroupsByType}
            fieldMappingId={fieldMappingId}
            formStore={formStore}
            knownFieldIds={isFieldsLoaded ? knownFieldIds : null}
            missingParentFields={missingParentFields}
          />
        </HideContainer>
      </Stack>
    );
  }
);

CustomFieldsEditor.displayName = 'CustomFieldsEditor';

type CustomFieldsSectionProps = {
  integrationShortId: string;
  configShortId: string;
  parentContext: Record<string, string>;
  rows: CustomFieldRow[];
  setRows: Dispatch<SetStateAction<CustomFieldRow[]>>;
  variableGroupsByType: (fieldType: FieldType) => FieldMappingGroup[];
  fieldMappingId: string;
  formStore: FormStoreInstance;
  knownFieldIds: Set<string> | null;
  missingParentFields: string[];
};

const CustomFieldsSection = ({
  integrationShortId,
  configShortId,
  parentContext,
  rows,
  setRows,
  variableGroupsByType,
  fieldMappingId,
  formStore,
  knownFieldIds,
  missingParentFields,
}: CustomFieldsSectionProps) => {
  const { t } = useScopedTranslate(locale);
  const showAddModal = useAddCustomFieldModal();
  const hasMissingParentFields = missingParentFields.length > 0;

  const handleAdd = () => {
    showAddModal({
      integrationShortId,
      configShortId,
      parentContext,
      existingFieldIds: rows.map((row) => row.fieldId),
      onAdd: (field: CustomField) => {
        setRows((previous) => [
          ...previous,
          {
            fieldId: field.fieldId,
            type: field.type,
            template: field.template,
            isArray: field.isArray,
            parentContextId: stableContextKey(parentContext),
            name: field.name,
            options: field.options,
          },
        ]);
        formStore.actions.setValue('__customFieldsTimestamp', Date.now());
      },
    });
  };

  const handleRemove = (fieldId: string) => {
    setRows((previous) => previous.filter((row) => row.fieldId !== fieldId));
    formStore.actions.unregister(customFieldRowFormId(fieldId));
    formStore.actions.setValue('__customFieldsTimestamp', Date.now());
  };

  if (hasMissingParentFields) {
    return (
      <InfoBox
        variant="indigo"
        icon={<IconFormIndicatorInfoCurrentColor />}
        text={t('selectToLoad', { fields: missingParentFields.join(', ') })}
      />
    );
  }

  return (
    <Stack space={4}>
      {rows.length > 0 && (
        <Stack space={3}>
          {rows.map((row) => {
            const optionsKey = (row.options ?? []).map((option) => option.id).join('|');
            return (
              <CustomFieldRowEditor
                key={`${row.fieldId}-${optionsKey}`}
                row={row}
                variableGroupsByType={variableGroupsByType}
                fieldMappingId={fieldMappingId}
                formStore={formStore}
                isDrift={knownFieldIds !== null && !knownFieldIds.has(row.fieldId)}
                onRemove={() => handleRemove(row.fieldId)}
              />
            );
          })}
        </Stack>
      )}
      <Stack orientation="horizontal" justify="flex-start">
        <Button
          text={t('addCustomField')}
          variant="primary-blue"
          iconLeft={<IconAddPlus />}
          onClick={handleAdd}
          data-testid="add-custom-field-button"
        />
      </Stack>
    </Stack>
  );
};

type CustomFieldRowEditorProps = {
  row: CustomFieldRow;
  variableGroupsByType: (fieldType: FieldType) => FieldMappingGroup[];
  fieldMappingId: string;
  formStore: FormStoreInstance;
  isDrift: boolean;
  onRemove: () => void;
};

const CustomFieldRowEditor = ({
  row,
  variableGroupsByType,
  fieldMappingId,
  formStore,
  isDrift,
  onRemove,
}: CustomFieldRowEditorProps) => {
  const { t } = useScopedTranslate(locale);
  const hasOptions = row.type === 'select' || row.type === 'array_of_strings';
  const field = {
    name: row.fieldId,
    label: row.name,
    type: row.type,
    visible: true,
    ...(hasOptions && {
      options: (row.options ?? []).map((option) => ({
        value: String(option.id ?? ''),
        label: option.label ?? '',
      })),
    }),
  } as IntegrationEndpointVariable;

  // A multi-select stores a list of options; the field reads its selection from
  // a {value: [ids]} shape.
  const defaultValue: VariableValue | MultiSelectFieldValue | undefined = Array.isArray(
    row.valueMapping
  )
    ? { value: row.valueMapping.map((item) => item.value) }
    : row.valueMapping;

  return (
    <Stack space={1} data-testid={`custom-field-row-${row.fieldId}`}>
      <Stack orientation="horizontal" space={2} align="end">
        <FieldColumn>
          <IntegrationField
            field={field}
            allFields={[field]}
            defaultValue={defaultValue}
            variableGroups={variableGroupsByType(row.type as FieldType)}
            prefix={CUSTOM_FIELDS_PREFIX}
            isNewAction={false}
            fieldMappingId={fieldMappingId}
            formStore={formStore}
          />
        </FieldColumn>
        <IconButton
          icon={<IconTrash />}
          variant="ghost-purple"
          tooltip={t('remove')}
          onClick={onRemove}
        />
      </Stack>
      {isDrift && (
        <Text font="regular12" color="red100" text={t('fieldRemoved', { field: row.fieldId })} />
      )}
    </Stack>
  );
};

type CustomFieldsDisplayProps = {
  endpointData: IntegrationEndpointDetail | undefined;
  customFields: SavedCustomField[];
};

// Read-only view: renders from the saved rows (names and labels are saved with
// each row), so it never re-fetches the integration's fields.
export const CustomFieldsDisplay = ({ endpointData, customFields }: CustomFieldsDisplayProps) => {
  const { t } = useScopedTranslate(locale);
  const configShortId = endpointData?.customFieldConfigShortId ?? null;
  const integrationShortId =
    typeof endpointData?.integration === 'string' ? endpointData.integration : '';

  if (!configShortId || !integrationShortId || customFields.length === 0) {
    return null;
  }

  return (
    <Stack space={4}>
      <Separator />
      <HideContainer title={t('customFields')} icon={<IconFieldCustom />}>
        <Stack space={3}>
          {customFields.map((row) => (
            <LabelTextGroup
              key={row.fieldId}
              label={row.name ?? row.fieldId}
              text={formatCustomFieldValue(row)}
            />
          ))}
        </Stack>
      </HideContainer>
    </Stack>
  );
};

const FieldColumn = styled.div({
  flex: 1,
  minWidth: 0,
});

const locale = {
  en: {
    customFields: 'Custom fields',
    addCustomField: 'Add custom field',
    remove: 'Remove',
    selectToLoad: 'Select {fields} to load available custom fields',
    fieldRemoved: 'Field "{field}" no longer exists in the integration. Remove or re-add this row.',
  },
};
